import { Check, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type { ModuleQuestion } from '../../domain/models';
import { useT } from '../../l10n/i18n';
import './hire.css';

// Form fields for what an employee asks the business (ADR 0010): the hiring form and the profile
// editor draw them the same way.

/** A field's error under it, tied to the input by `aria-describedby`. */
export function FieldError({ id, error }: { id: string; error?: string }) {
  if (!error) return null;
  return (
    <p id={`${id}-error`} className="field-error">
      {error}
    </p>
  );
}

const describedBy = (...ids: (string | false | undefined)[]) => ids.filter(Boolean).join(' ') || undefined;

/** The label's words, with a quiet mark on required fields (screen readers hear `aria-required`). */
function LabelText({ label, required }: { label: string; required: boolean }) {
  const t = useT();
  return (
    <>
      <span>{label}</span>
      {required && (
        <span className="hire-required" aria-hidden>
          {t('hireRequired')}
        </span>
      )}
    </>
  );
}

/** One question as a field by its kind: a line, several lines, an address, or one of a few options. */
export function QuestionField({
  id,
  question: q,
  value,
  onChange,
  error,
  disabled = false,
}: {
  id: string;
  question: ModuleQuestion;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  disabled?: boolean;
}) {
  const helpId = q.help ? `${id}-help` : undefined;
  const described = describedBy(helpId, error && `${id}-error`);
  const help = q.help && (
    <p id={helpId} className="body-small hire-help">
      {q.help}
    </p>
  );

  if (q.kind === 'choice' && q.options.length > 0) {
    return (
      <fieldset className="hire-field" aria-describedby={described} aria-invalid={error ? true : undefined} disabled={disabled}>
        <legend className="hire-label">
          <LabelText label={q.label} required={q.required} />
        </legend>
        {help}
        <div className="hire-chips" role="radiogroup" aria-required={q.required}>
          {q.options.map((o, i) => (
            <label key={o} className="hire-chip">
              <input
                id={i === 0 ? id : undefined}
                type="radio"
                name={id}
                value={o}
                checked={value === o}
                onChange={() => onChange(o)}
              />
              <Check size={15} strokeWidth={2.6} className="hire-chip-check" aria-hidden />
              <span>{o}</span>
            </label>
          ))}
        </div>
        <FieldError id={id} error={error} />
      </fieldset>
    );
  }

  const common = {
    id,
    value,
    disabled,
    'aria-required': q.required,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': described,
  };
  return (
    <div className="hire-field">
      <label htmlFor={id} className="hire-label">
        <LabelText label={q.label} required={q.required} />
      </label>
      {help}
      {q.kind === 'long' ? (
        <textarea className="textarea hire-textarea" rows={3} {...common} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input
          className="input"
          type={q.kind === 'url' ? 'url' : 'text'}
          inputMode={q.kind === 'url' ? 'url' : undefined}
          placeholder={q.kind === 'url' ? 'https://' : undefined}
          autoComplete="off"
          {...common}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      <FieldError id={id} error={error} />
    </div>
  );
}

/** A plain field of the form's own (a project's name and description, a note), with an optional hint. */
export function TextField({
  id,
  label,
  help,
  value,
  onChange,
  error,
  required = false,
  long = false,
  rows = 3,
  maxLength,
  disabled = false,
}: {
  id: string;
  label: string;
  help?: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  required?: boolean;
  long?: boolean;
  rows?: number;
  maxLength?: number;
  disabled?: boolean;
}) {
  const helpId = help ? `${id}-help` : undefined;
  const common = {
    id,
    value,
    maxLength,
    disabled,
    'aria-required': required || undefined,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy(helpId, error && `${id}-error`),
  };
  return (
    <div className="hire-field">
      <label htmlFor={id} className="hire-label">
        <LabelText label={label} required={required} />
      </label>
      {help && (
        <p id={helpId} className="body-small hire-help">
          {help}
        </p>
      )}
      {long ? (
        <textarea className="textarea hire-textarea" rows={rows} {...common} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className="input" type="text" autoComplete="off" {...common} onChange={(e) => onChange(e.target.value)} />
      )}
      <FieldError id={id} error={error} />
    </div>
  );
}

/** A choice that is on or off, as a chip: a connection to set up. */
export function CheckChip({
  label,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  checked: boolean;
  onChange: (on: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="hire-chip">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <Check size={15} strokeWidth={2.6} className="hire-chip-check" aria-hidden />
      <span>{label}</span>
    </label>
  );
}

/** A kind of work to tick, with its icon as in the inbox. */
export function CheckRow({
  icon: Icon,
  label,
  detail,
  checked,
  onChange,
}: {
  icon: LucideIcon;
  label: string;
  detail?: ReactNode;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label className={checked ? 'hire-row on' : 'hire-row'}>
      <span className="hire-row-tile" aria-hidden>
        <Icon size={18} strokeWidth={2} />
      </span>
      <span className="hire-row-text">
        <span className="title-small">{label}</span>
        {detail && <span className="body-small">{detail}</span>}
      </span>
      <input type="checkbox" className="hire-checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}
